/**
 * BooleanFilterPill
 * =================
 * A removable chip for a boolean dimension (e.g. "bez zdjęcia" / no-photo).
 *
 * UX model (Shopify Polaris "appliedFilters"): a boolean filter has no meaningful
 * "added but not applied" state. Adding it from the "Dodaj filtr" menu IS applying
 * it, so this component renders ONLY the applied chip — label + clear (X). There is
 * no idle state, no chevron, and no toggle-on-click (those belong to popover-backed
 * pills). The chip is value-derived: it is mounted only while the filter is active,
 * and its single affordance (X) clears the value and returns the item to the menu.
 *
 * Register it in FilterPanel via extraHiddenItems + renderExtraPill +
 * defaultVisibleExtraIds, and apply the value on add via onShowExtra.
 *
 * Translation-free: all labels are passed as props.
 */

import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/utils.js';
import {
  PILL_BUTTON_ACTIVE,
  PILL_BUTTON_BASE,
  PILL_VALUE_CLASS,
  PillWrapper,
} from './filter-pill-primitives.js';

export interface BooleanFilterPillProps {
  /** Field label, shown on the applied chip. */
  label: string;
  /** Called when the user clears the filter via the X control. */
  onClear: () => void;
  /** Accessible label for the clear (X) control. */
  clearAriaLabel: string;
  /** Base data-testid; the chip uses `${testId}-trigger`, the clear control `${testId}-clear`. */
  testId: string;
}

export function BooleanFilterPill({
  label,
  onClear,
  clearAriaLabel,
  testId,
}: BooleanFilterPillProps) {
  return (
    <PillWrapper label={label} showLabel>
      <div
        className={cn(PILL_BUTTON_BASE, PILL_BUTTON_ACTIVE, 'cursor-default')}
        data-testid={`${testId}-trigger`}
      >
        <span className="flex items-center gap-1 text-[11px] text-foreground">
          <span className={PILL_VALUE_CLASS}>{label}</span>
        </span>
        <button
          type="button"
          aria-label={clearAriaLabel}
          data-testid={`${testId}-clear`}
          onClick={onClear}
          className="shrink-0 hover:opacity-70 rounded-full p-0.5"
        >
          <X className="h-3 w-3 text-foreground/60" />
        </button>
      </div>
    </PillWrapper>
  );
}
